import { ProjectGlobalData, ServiceItem } from '../../types';
import { contractedQuantity, isNepService } from '../../services/nepPosition';
import { isAccountingEntry } from '../../services/serviceLogUtils';
import { istPlanungsleistung } from '../../services/leistungsPhase';
import { tageBis } from './projectDataHygiene';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PROJEKTSTAND IM HUB — die reine Logik (docs/projektstand-hub-plan.md)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Betreiber 25.09.2026: beim Oeffnen eines Projekts soll man auf einen Blick
 * sehen, wie weit es ist — und zwar NUR an zwei Dingen: Laufzeit und
 * Leistungen. Maengel, Termine, Dokumentstatus gehoeren ausdruecklich nicht
 * hinein.
 *
 * Nur lesend: alles kommt aus globalData und dem (ggf. Mutter-)LV, das im
 * Hub ohnehin vorliegt. Keine Abfrage, kein Schreiben. Die Oberflaeche steckt
 * in ProjectStandKarte.
 */

// ─── Laufzeit ───────────────────────────────────────────────────────────────

export type LaufzeitZustand =
    | 'fehlt'           // Start oder Ende nicht (gueltig) gepflegt
    | 'vor_start'       // heute liegt vor dem Start
    | 'laeuft'
    | 'ueberschritten'  // Ende liegt zurueck, Projekt nicht beendet
    | 'ruht'            // Status on_hold
    | 'beendet';        // Status completed / finished

export type PhaseArt = 'planung' | 'ausfuehrung';

export const PHASE_NAME: Record<PhaseArt, string> = { planung: 'Planung', ausfuehrung: 'Ausführung' };

/** Eine Phase im Zeitplan: Planungsphase oder Ausfuehrungsphase. */
export interface LaufzeitAbschnitt {
    art: PhaseArt;
    /** Lage im Balken, 0–100 (auf die Laufzeit begrenzt). */
    von: number;
    bis: number;
    /** Die Daten der Phase (YYYY-MM-DD bzw. wie gepflegt). */
    vonTag: string;
    bisTag: string;
    /** Verstrichener Anteil DIESER Phase, 0–100. */
    zeitProzent: number;
    zustand: 'kommt' | 'laeuft' | 'vorbei';
    /** Tage bis zum Phasenende (negativ = vorbei). */
    restTage: number;
    /** Tage bis zum Phasenbeginn (positiv = kommt noch). */
    tageBisStart: number;
}

export interface Laufzeit {
    zustand: LaufzeitZustand;
    /** Wie gepflegt (YYYY-MM-DD oder DD.MM.YYYY); null = fehlt. */
    start: string | null;
    ende: string | null;
    /** Verstrichener Anteil 0–100; null, wenn Start/Ende fehlen. */
    prozent: number | null;
    /** Tage bis zum Ende (negativ = seit dem Ende); null, wenn Ende fehlt. */
    restTage: number | null;
    /** Tage bis zum Start (nur sinnvoll bei 'vor_start'). */
    tageBisStart: number | null;
    woche: number | null;
    wochenGesamt: number | null;
    /** Planungs- und Ausfuehrungsphase, soweit gepflegt; leer = keine Phasen. */
    abschnitte: LaufzeitAbschnitt[];
    /** Die Phase, in der heute liegt; null ausserhalb beider oder beendet. */
    aktuellePhase: PhaseArt | null;
}

type LaufzeitDaten = Pick<ProjectGlobalData,
    'status' | 'startDate' | 'endDate' | 'planningStartDate' | 'planningEndDate' | 'executionStartDate' | 'executionEndDate'>;

const istDatum = (s: string | undefined | null): s is string => tageBis(s || '', s) !== null;
const erstesDatum = (...werte: (string | undefined | null)[]): string | null => werte.find(istDatum) ?? null;
const begrenzt = (n: number, min = 0, max = 100) => Math.min(max, Math.max(min, n));

/**
 * Start = Projektstart, sonst Beginn der Planung, sonst Beginn der Ausfuehrung.
 * Ende = geplantes Ende, sonst Ende der Ausfuehrung. `heute` ist der LOKALE
 * Kalendertag (heuteLokal) — toISOString kippt abends auf den Folgetag.
 */
export const berechneLaufzeit = (g: LaufzeitDaten | undefined, heute: string): Laufzeit => {
    const start = erstesDatum(g?.startDate, g?.planningStartDate, g?.executionStartDate);
    const ende = erstesDatum(g?.endDate, g?.executionEndDate);
    const beendet = g?.status === 'completed' || g?.status === 'finished';

    const leer: Laufzeit = {
        zustand: beendet ? 'beendet' : 'fehlt', start, ende, prozent: null,
        restTage: null, tageBisStart: null, woche: null, wochenGesamt: null, abschnitte: [], aktuellePhase: null,
    };
    if (!start || !ende) return leer;

    const gesamt = tageBis(start, ende)!;
    // Ende vor dem Start ist ein Tippfehler, keine Laufzeit.
    if (gesamt < 0) return leer;
    const vergangen = tageBis(start, heute) ?? 0;
    const restTage = tageBis(heute, ende);
    const prozent = gesamt > 0
        ? begrenzt((vergangen / gesamt) * 100)
        : (vergangen >= 0 ? 100 : 0);

    // Die Phasen. Eine fehlende Grenze wird aus der Nachbarphase bzw. der
    // Laufzeit ergaenzt: wer nur „Ausführung ab 10.04." pflegt, hat davor
    // eine Planungsphase ab Projektstart. Ganz ohne Phasenangaben gibt es
    // keine Phasen — dann bleibt es beim einen Balken.
    const planungBis = erstesDatum(g?.planningEndDate, g?.executionStartDate);
    const planungVon = erstesDatum(g?.planningStartDate) ?? (planungBis ? start : null);
    const ausfuehrungVon = erstesDatum(g?.executionStartDate, g?.planningEndDate);
    const ausfuehrungBis = erstesDatum(g?.executionEndDate) ?? (ausfuehrungVon ? ende : null);

    const phase = (von: string | null, bis: string | null, art: PhaseArt): LaufzeitAbschnitt | null => {
        if (!von || !bis || gesamt <= 0) return null;
        const dauer = tageBis(von, bis)!;
        if (dauer <= 0) return null;
        const a = begrenzt((tageBis(start, von)! / gesamt) * 100);
        const b = begrenzt((tageBis(start, bis)! / gesamt) * 100);
        if (b <= a) return null;
        const seitBeginn = tageBis(von, heute) ?? 0;
        const restPhase = tageBis(heute, bis) ?? 0;
        const zustandPhase: LaufzeitAbschnitt['zustand'] = beendet ? 'vorbei'
            : seitBeginn < 0 ? 'kommt'
            : restPhase < 0 ? 'vorbei'
            : 'laeuft';
        return {
            art, von: a, bis: b, vonTag: von, bisTag: bis,
            zeitProzent: beendet ? 100 : begrenzt((seitBeginn / dauer) * 100),
            zustand: zustandPhase,
            restTage: restPhase,
            tageBisStart: -seitBeginn,
        };
    };
    const abschnitte = [
        phase(planungVon, planungBis, 'planung'),
        phase(ausfuehrungVon, ausfuehrungBis, 'ausfuehrung'),
    ].filter((x): x is LaufzeitAbschnitt => x !== null);
    // Ueberlappen beide Phasen heute, zaehlt die Ausfuehrung — auf der
    // Baustelle ist dann schon Betrieb.
    const laufende = abschnitte.filter(a => a.zustand === 'laeuft');
    const aktuellePhase = laufende.length ? laufende[laufende.length - 1].art : null;

    const wochenGesamt = Math.max(1, Math.ceil((gesamt + 1) / 7));
    const woche = vergangen < 0 ? null : begrenzt(Math.floor(vergangen / 7) + 1, 1, wochenGesamt);

    const zustand: LaufzeitZustand = beendet ? 'beendet'
        : g?.status === 'on_hold' ? 'ruht'
        : vergangen < 0 ? 'vor_start'
        : (restTage ?? 0) < 0 ? 'ueberschritten'
        : 'laeuft';

    return {
        zustand, start, ende,
        prozent: beendet ? 100 : prozent,
        restTage,
        tageBisStart: vergangen < 0 ? -vergangen : null,
        woche, wochenGesamt, abschnitte, aktuellePhase,
    };
};

/**
 * Wo der Zeitstrich im Leistungsbalken EINER Phase sitzt: nur solange die
 * Phase laeuft oder vorbei ist (dann am Ende) — vor ihrem Beginn sagt er
 * nichts, und nach dem Beenden des Projekts auch nicht.
 */
export const phasenLinie = (p: LaufzeitAbschnitt | undefined, l: Laufzeit): number | null =>
    !p || p.zustand === 'kommt' || l.zustand === 'beendet' ? null : p.zeitProzent;

/**
 * Wo die Laufzeit-Linie in den Leistungsbalken sitzt — oder null, wenn sie
 * nichts aussagt: ohne Laufzeit, vor dem Start und nach dem Beenden.
 */
export const laufzeitLinie = (l: Laufzeit): number | null =>
    (l.zustand === 'laeuft' || l.zustand === 'ruht' || l.zustand === 'ueberschritten') ? l.prozent : null;

// ─── Leistungen ─────────────────────────────────────────────────────────────

export interface LeistungsSumme {
    totalContracted: number;
    totalUsed: number;
    totalQtyContracted: number;
    totalQtyUsed: number;
}

/**
 * Die Gesamtrechnung von „Leistung & Budget" — EINE Stelle fuer die Karte in
 * ProjectStats und den Projektstand, damit beide dieselbe Zahl zeigen.
 * NEP zaehlt in „beauftragt" erst, was gebucht ist (contractedQuantity).
 */
export const leistungsSumme = (services: ServiceItem[]): LeistungsSumme => {
    let totalContracted = 0, totalUsed = 0, totalQtyContracted = 0, totalQtyUsed = 0;
    services.forEach(s => {
        const soll = contractedQuantity(s);
        const ist = s.quantityUsed || 0;
        totalContracted += soll * (s.unitPrice || 0);
        totalUsed += ist * (s.unitPrice || 0);
        totalQtyContracted += soll;
        totalQtyUsed += ist;
    });
    return { totalContracted, totalUsed, totalQtyContracted, totalQtyUsed };
};

export const anteil = (ist: number, soll: number): number => (soll > 0 ? (ist / soll) * 100 : 0);

export type LeistungsZustand = 'offen' | 'laeuft' | 'erledigt' | 'ueber' | 'ohne_soll' | 'nep';

/** Phase einer LV-Position — die gemeinsame Regel aus leistungsPhase.ts. */
export const phaseVon = (s: ServiceItem): PhaseArt => (istPlanungsleistung(s) ? 'planung' : 'ausfuehrung');

/** Die Gesamtrechnung getrennt nach Planung und Ausfuehrung; `anzahl` = Positionen der Phase. */
export const leistungsSummeJePhase = (services: ServiceItem[]): Record<PhaseArt, LeistungsSumme & { anzahl: number }> => {
    const je = (art: PhaseArt) => {
        const teil = services.filter(s => phaseVon(s) === art);
        return { ...leistungsSumme(teil), anzahl: teil.length };
    };
    return { planung: je('planung'), ausfuehrung: je('ausfuehrung') };
};

export interface LeistungsZeile {
    service: ServiceItem;
    phase: PhaseArt;
    used: number;
    contracted: number;
    /** 0–∞ (ueber 100 bei Mehrmenge); null ohne Soll und bei NEP. */
    prozent: number | null;
    zustand: LeistungsZustand;
}

/**
 * Eine Zeile je LV-Position in LV-Reihenfolge; NEP-Positionen ans Ende —
 * sie sind nicht beauftragt und haben keinen Nenner.
 */
export const leistungsZeilen = (services: ServiceItem[]): LeistungsZeile[] => {
    const zeile = (s: ServiceItem): LeistungsZeile => {
        const used = s.quantityUsed || 0;
        const phase = phaseVon(s);
        if (isNepService(s)) return { service: s, phase, used, contracted: 0, prozent: null, zustand: 'nep' };
        const contracted = s.quantityContracted || 0;
        if (contracted <= 0) return { service: s, phase, used, contracted, prozent: null, zustand: 'ohne_soll' };
        const zustand: LeistungsZustand = used > contracted ? 'ueber'
            : used >= contracted ? 'erledigt'
            : used > 0 ? 'laeuft'
            : 'offen';
        return { service: s, phase, used, contracted, prozent: anteil(used, contracted), zustand };
    };
    const alle = services.map(zeile);
    return [...alle.filter(z => z.zustand !== 'nep'), ...alle.filter(z => z.zustand === 'nep')];
};

// ─── Verlauf: Leistung ueber die Laufzeit ───────────────────────────────────

/** YYYY-MM-DD (auch mit Zeitanteil) bzw. DD.MM.YYYY → YYYY-MM-DD; null bei allem anderen. */
export const isoTag = (s: string | null | undefined): string | null => {
    const t = (s || '').trim();
    let m = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    m = t.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
    return null;
};

export interface VerlaufPunkt {
    /** YYYY-MM-DD */
    tag: string;
    /** Kumulierter Anteil am Soll nach diesem Tag, in Prozent. */
    prozent: number;
}

export interface LeistungsVerlauf {
    /** Ein Punkt je Buchungstag, kumuliert, aufsteigend. */
    punkte: VerlaufPunkt[];
    /** Sichtbarer Zeitraum der x-Achse (YYYY-MM-DD). */
    von: string;
    bis: string;
    /** Gleichmaessiger Verlauf 0 → 100 % von Start bis Ende; null ohne Laufzeit. */
    soll: { von: string; bis: string } | null;
    /** Heute, wenn es im Zeitraum liegt und das Projekt nicht beendet ist. */
    heute: string | null;
    /** Stand laut Zaehler — dieselbe Zahl wie „Leistung & Budget". */
    endProzent: number;
    /** Obergrenze der y-Achse, mindestens 100. */
    maxProzent: number;
}

/**
 * Wie die Leistung ueber die Zeit gewachsen ist: das Buchungsprotokoll
 * kumuliert, gemessen am heutigen Soll (Innendienst in Euro, sonst Menge —
 * wie der Gesamtbalken). Abrechnungszeilen (BU-/VZ-/RN-/MB-) sind keine
 * Leistung und zaehlen nicht (isAccountingEntry, wie loggedQuantity).
 *
 * Das Protokoll ist die Kurve, der ZAEHLER ist der Stand: `endProzent` kommt
 * aus den Positionen, genau wie der Gesamtbalken. Weicht das Protokoll davon
 * ab (Altbestand ohne Protokoll, Rueckschreibungen), macht die Linie heute
 * den Schritt auf den Zaehlerstand — sie endet nie woanders als der Balken.
 *
 * null, wenn es nichts zu zeichnen gibt: kein Soll, oder weder Laufzeit noch
 * Buchung.
 */
export const leistungsVerlauf = ({ services, log, inEuro, laufzeit, heute }: {
    services: ServiceItem[];
    log: ReadonlyArray<{ date?: string; serviceId?: string; quantity?: number; bookingReference?: string }>;
    inEuro: boolean;
    laufzeit: Laufzeit;
    heute: string;
}): LeistungsVerlauf | null => {
    const summe = leistungsSumme(services);
    const soll = inEuro ? summe.totalContracted : summe.totalQtyContracted;
    if (soll <= 0) return null;
    const ist = inEuro ? summe.totalUsed : summe.totalQtyUsed;

    const gewicht = new Map(services.map(s => [s.id, inEuro ? (s.unitPrice || 0) : 1]));
    const jeTag = new Map<string, number>();
    for (const t of log) {
        if (isAccountingEntry(t) || !t.serviceId || !gewicht.has(t.serviceId)) continue;
        const tag = isoTag(t.date);
        if (!tag) continue;
        const wert = (Number(t.quantity) || 0) * gewicht.get(t.serviceId)!;
        if (wert === 0) continue;
        jeTag.set(tag, (jeTag.get(tag) || 0) + wert);
    }
    let kumuliert = 0;
    const punkte: VerlaufPunkt[] = [...jeTag.keys()].sort().map(tag => {
        kumuliert += jeTag.get(tag)!;
        return { tag, prozent: anteil(kumuliert, soll) };
    });

    const start = isoTag(laufzeit.start);
    const ende = isoTag(laufzeit.ende);
    const mitLaufzeit = laufzeit.prozent !== null && !!start && !!ende;
    const beendet = laufzeit.zustand === 'beendet';

    // Heute gehoert in einem laufenden Projekt IMMER auf die Achse — auch vor
    // dem Start: sonst reichte die Ist-Linie bis in die Zukunft.
    const kandidatenVon = [mitLaufzeit ? start : null, punkte[0]?.tag ?? null, beendet ? null : heute].filter((x): x is string => !!x);
    const kandidatenBis = [mitLaufzeit ? ende : null, beendet ? null : heute, punkte[punkte.length - 1]?.tag ?? null]
        .filter((x): x is string => !!x);
    if (!mitLaufzeit && punkte.length === 0) return null;
    const von = kandidatenVon.length ? kandidatenVon.sort()[0] : heute;
    const bis = kandidatenBis.sort()[kandidatenBis.length - 1] ?? von;

    const endProzent = anteil(ist, soll);
    const maxProzent = Math.max(100, endProzent, ...punkte.map(p => p.prozent));

    return {
        punkte, von, bis,
        soll: mitLaufzeit ? { von: start!, bis: ende! } : null,
        heute: !beendet && heute >= von && heute <= bis ? heute : null,
        endProzent, maxProzent,
    };
};

// ─── Ansicht: Gesamt, Planung oder Ausfuehrung ──────────────────────────────

export type StandAnsicht = 'gesamt' | PhaseArt;

export interface StandDaten {
    ansicht: StandAnsicht;
    /** Zeitanteil der Ansicht (Laufzeit bzw. Phase), 0–100; null = nicht gepflegt. */
    zeitProzent: number | null;
    zeitVon: string | null;
    zeitBis: string | null;
    /** Kurztext zur Zeit: „noch 84 Tage", „abgelaufen", „nicht gepflegt" … */
    zeitText: string;
    /** Ende ueberschritten — der Text wird rot. */
    zeitWarnung: boolean;
    summe: LeistungsSumme;
    zeilen: LeistungsZeile[];
    verlauf: LeistungsVerlauf | null;
    /** Zeitstrich in den Leistungsbalken dieser Ansicht; null = keiner. */
    linie: number | null;
}

const tageText = (n: number) => `${n} ${n === 1 ? 'Tag' : 'Tagen'}`;
const nochText = (n: number) => (n === 0 ? 'endet heute' : `noch ${n} ${n === 1 ? 'Tag' : 'Tage'}`);

/**
 * Alles, was die Karte fuer EINE Ansicht zeigt. Gesamt = ganze Laufzeit und
 * ganzes LV; Planung/Ausfuehrung = der Zeitraum der Phase und nur ihre
 * Positionen (istPlanungsleistung). Die Kurve einer Phase misst gegen das
 * Soll DIESER Phase und laeuft gleichmaessig ueber IHREN Zeitraum.
 */
export const standFuerAnsicht = ({ services, log, laufzeit, heute, inEuro }: {
    services: ServiceItem[];
    log: ReadonlyArray<{ date?: string; serviceId?: string; quantity?: number; bookingReference?: string }>;
    laufzeit: Laufzeit;
    heute: string;
    inEuro: boolean;
}, ansicht: StandAnsicht): StandDaten => {
    const beendet = laufzeit.zustand === 'beendet';

    if (ansicht === 'gesamt') {
        const l = laufzeit;
        const zeitText = l.zustand === 'fehlt' ? 'nicht gepflegt'
            : l.zustand === 'vor_start' ? `beginnt in ${tageText(l.tageBisStart ?? 0)}`
            : l.zustand === 'ueberschritten' ? `seit ${tageText(-(l.restTage ?? 0))} überschritten`
            : l.zustand === 'ruht' ? 'ruht'
            : l.zustand === 'beendet' ? 'beendet'
            : nochText(l.restTage ?? 0);
        return {
            ansicht, zeitProzent: l.prozent, zeitVon: l.start, zeitBis: l.ende, zeitText,
            zeitWarnung: l.zustand === 'ueberschritten',
            summe: leistungsSumme(services),
            zeilen: leistungsZeilen(services),
            verlauf: leistungsVerlauf({ services, log, inEuro, laufzeit: l, heute }),
            linie: laufzeitLinie(l),
        };
    }

    const teil = services.filter(s => phaseVon(s) === ansicht);
    const phase = laufzeit.abschnitte.find(a => a.art === ansicht);
    // Die Phase als eigene „Laufzeit" fuer die Kurve: vorbei zaehlt wie
    // ueberschritten (die Achse reicht bis heute), damit sichtbar bleibt,
    // was nach dem Phasenende noch gebucht wurde (z. B. die Unterlage).
    const phasenLaufzeit: Laufzeit = phase ? {
        ...laufzeit,
        zustand: beendet ? 'beendet' : phase.zustand === 'kommt' ? 'vor_start' : phase.zustand === 'vorbei' ? 'ueberschritten' : 'laeuft',
        start: phase.vonTag, ende: phase.bisTag,
        prozent: phase.zeitProzent, restTage: phase.restTage,
        tageBisStart: phase.zustand === 'kommt' ? phase.tageBisStart : null,
        abschnitte: [], aktuellePhase: null,
    } : { ...laufzeit, zustand: beendet ? 'beendet' : 'fehlt', start: null, ende: null, prozent: null, abschnitte: [], aktuellePhase: null };

    const zeitText = !phase ? 'Zeitraum nicht gepflegt'
        : beendet ? 'beendet'
        : phase.zustand === 'kommt' ? `beginnt in ${tageText(phase.tageBisStart)}`
        : phase.zustand === 'vorbei' ? 'abgelaufen'
        : nochText(phase.restTage);

    return {
        ansicht,
        zeitProzent: phase ? phase.zeitProzent : null,
        zeitVon: phase?.vonTag ?? null,
        zeitBis: phase?.bisTag ?? null,
        zeitText,
        zeitWarnung: false,
        summe: leistungsSumme(teil),
        zeilen: leistungsZeilen(teil),
        verlauf: leistungsVerlauf({ services: teil, log, inEuro, laufzeit: phasenLaufzeit, heute }),
        linie: phasenLinie(phase, laufzeit),
    };
};

// ─── Auswertung: Plan-Abstand, Tempo, Prognose ──────────────────────────────

/** Mittlere Monatslaenge in Tagen (365,25 / 12). */
export const TAGE_PRO_MONAT = 30.44;
/** Fenster fuer das Tempo: die letzten 90 Tage. */
export const TEMPO_FENSTER_TAGE = 90;
/** Unter so vielen Tagen Beobachtung ist ein Tempo nur Rauschen. */
const TEMPO_MIN_TAGE = 14;

/** Kumulierter Stand laut Protokoll an einem Tag (letzter Buchungstag davor). */
export const protokollStandAm = (punkte: VerlaufPunkt[], tag: string): number => {
    let wert = 0;
    for (const p of punkte) {
        if (p.tag > tag) break;
        wert = p.prozent;
    }
    return wert;
};

export interface StandPrognose {
    /** Ist minus gleichmaessig, heute, in Prozentpunkten; null ohne Zeitraum. */
    abweichung: number | null;
    /** Ø Prozentpunkte pro Monat in den letzten 90 Tagen; null ohne genug Beobachtung. */
    tempoProMonat: number | null;
    /** Prozentpunkte pro Monat, die bis zum Ende noch noetig sind; null ohne Ende oder danach. */
    noetigProMonat: number | null;
    /** Erwarteter Stand am Ende bei gleichem Tempo; null ohne Tempo oder Ende. */
    prognoseEnde: number | null;
    /** Der Endtag der Prognose (YYYY-MM-DD). */
    prognoseTag: string | null;
}

/**
 * Was aus der Kurve folgt. Das Tempo kommt aus dem PROTOKOLL (nicht aus dem
 * Zaehlerstand), damit der Abgleichschritt „heute" (Altbestand ohne
 * Buchungsdatum) nicht als Leistung der letzten Wochen zaehlt. Die Prognose
 * setzt am Zaehlerstand an und schreibt das Tempo bis zum Ende fort.
 */
export const standPrognose = (verlauf: LeistungsVerlauf | null, heute: string): StandPrognose => {
    const leer: StandPrognose = { abweichung: null, tempoProMonat: null, noetigProMonat: null, prognoseEnde: null, prognoseTag: null };
    if (!verlauf) return leer;
    const { soll, punkte, endProzent } = verlauf;

    const plan = soll
        ? (heute <= soll.von ? 0 : heute >= soll.bis ? 100 : tagAnteil(soll.von, soll.bis, heute) * 100)
        : null;
    const abweichung = plan === null ? null : endProzent - plan;

    // Fenster [fensterStart, heute] — BEIDE Tage eingeschlossen, also
    // tage + 1 Kalendertage: der Zuwachs zaehlt die Buchungen am Fensterbeginn
    // mit, der Nenner muss denselben Tag mitzaehlen.
    let tempoProTag: number | null = null;
    if (punkte.length > 0) {
        const erster = punkte[0].tag;
        const fensterStart = [erster, tagMinus(heute, TEMPO_FENSTER_TAGE - 1)].sort()[1];
        const tage = tageBis(fensterStart, heute) ?? 0;
        if (tage >= TEMPO_MIN_TAGE) {
            const zuwachs = protokollStandAm(punkte, heute) - protokollStandAm(punkte, tagMinus(fensterStart, 1));
            tempoProTag = Math.max(0, zuwachs) / (tage + 1);
        }
    }

    const restTage = soll ? tageBis(heute, soll.bis) : null;
    const offen = restTage !== null && restTage > 0;
    return {
        abweichung,
        tempoProMonat: tempoProTag === null ? null : tempoProTag * TAGE_PRO_MONAT,
        noetigProMonat: offen ? (Math.max(0, 100 - endProzent) / restTage!) * TAGE_PRO_MONAT : null,
        prognoseEnde: offen && tempoProTag !== null ? endProzent + tempoProTag * restTage! : null,
        prognoseTag: offen ? soll!.bis : null,
    };
};

const zahl = (n: number, stellen = 0) =>
    new Intl.NumberFormat('de-DE', { maximumFractionDigits: stellen, minimumFractionDigits: stellen }).format(n);

/**
 * Der eine Satz unter den Ringen: wo die Leistung gegen die Zeit steht und
 * wohin das Tempo fuehrt. Nur Aussagen, die die Zahlen tragen — fehlt
 * Zeitraum oder Tempo, faellt der jeweilige Teil weg.
 */
export const standSatz = (p: StandPrognose): string => {
    const teile: string[] = [];
    if (p.abweichung !== null) {
        const a = Math.round(p.abweichung);
        teile.push(a <= -3 ? `Die Leistung liegt ${-a} Punkte hinter der Zeit.`
            : a >= 3 ? `Die Leistung liegt ${a} Punkte vor der Zeit.`
            : 'Leistung und Zeit laufen gleichauf.');
    }
    if (p.prognoseEnde !== null && p.tempoProMonat !== null) {
        const ende = Math.round(p.prognoseEnde);
        if (ende > 105) {
            teile.push(`Bei gleichem Tempo ist das Soll vor dem Ende aufgebraucht (${ende} %).`);
        } else if (p.noetigProMonat !== null && p.tempoProMonat < p.noetigProMonat * 0.9) {
            teile.push(`Bei gleichem Tempo ${ende} % zum Ende — nötig wären ${zahl(p.noetigProMonat, 1)} % pro Monat statt ${zahl(p.tempoProMonat, 1)} %.`);
        } else {
            teile.push(`Bei gleichem Tempo ${ende} % zum Ende.`);
        }
    }
    return teile.join(' ') || 'Noch zu wenig gebucht für eine Aussage zum Verlauf.';
};

export interface MonatsWert {
    /** YYYY-MM */
    monat: string;
    /** Zuwachs in Prozentpunkten des Solls. */
    prozent: number;
}

/**
 * Leistung je Monat aus dem Protokoll — jeder Monat von Beginn bis heute
 * (bzw. Ende), auch die leeren: gerade die Luecken sind die Aussage. Der
 * Abgleichschritt auf den Zaehlerstand gehoert zu keinem Monat; er kommt als
 * `ohneDatum` zurueck und wird getrennt genannt.
 */
export const leistungJeMonat = (verlauf: LeistungsVerlauf): { monate: MonatsWert[]; ohneDatum: number } => {
    const bisTag = verlauf.heute && verlauf.heute < verlauf.bis ? verlauf.heute : verlauf.bis;
    const monate: MonatsWert[] = [];
    let [y, m] = verlauf.von.slice(0, 7).split('-').map(Number);
    const [yEnde, mEnde] = bisTag.slice(0, 7).split('-').map(Number);
    while (y < yEnde || (y === yEnde && m <= mEnde)) {
        monate.push({ monat: `${y}-${String(m).padStart(2, '0')}`, prozent: 0 });
        m += 1;
        if (m > 12) { m = 1; y += 1; }
    }
    let vorher = 0;
    for (const p of verlauf.punkte) {
        const eintrag = monate.find(x => x.monat === p.tag.slice(0, 7));
        if (eintrag) eintrag.prozent += p.prozent - vorher;
        vorher = p.prozent;
    }
    const letzter = verlauf.punkte[verlauf.punkte.length - 1]?.prozent ?? 0;
    return { monate, ohneDatum: verlauf.endProzent - letzter };
};

export interface PositionsPrognose {
    text: string;
    /** Das Soll ist vor dem Ende aufgebraucht — Nachtrag noetig. */
    knapp: boolean;
}

/**
 * Hochrechnung EINER Mengenposition (Begehungen, Stunden, Fahrten): Tempo
 * seit der ersten Buchung, fortgeschrieben bis zum Ende ihrer Phase bzw. der
 * Laufzeit. Nur fuer laufende Positionen mit Soll > 1 und genug Beobachtung.
 *
 * Das Tempo kommt NUR aus den datierten Buchungen; der Zaehler ist der
 * Ausgangsstand. Sonst liefe undatierter Altbestand (50 Einheiten aus der
 * Uebernahme, eine Buchung vor 24 Tagen) als 2 Einheiten pro Tag in die
 * Prognose.
 */
export const positionsPrognose = (
    z: LeistungsZeile,
    log: ReadonlyArray<{ date?: string; serviceId?: string; quantity?: number; bookingReference?: string }>,
    heute: string,
    ende: string | null,
): PositionsPrognose | null => {
    if (z.zustand !== 'laeuft' || z.contracted <= 1 || !ende) return null;
    const restTage = tageBis(heute, ende);
    if (restTage === null || restTage < 0) return null;
    const buchungen = log
        .filter(t => t.serviceId === z.service.id && !isAccountingEntry(t))
        .map(t => ({ tag: isoTag(t.date), menge: Number(t.quantity) || 0 }))
        .filter((t): t is { tag: string; menge: number } => !!t.tag && t.tag <= heute && t.menge !== 0);
    const erster = buchungen.filter(b => b.menge > 0).map(b => b.tag).sort()[0];
    if (!erster) return null;
    const beobachtet = tageBis(erster, heute) ?? 0;
    if (beobachtet < TEMPO_MIN_TAGE) return null;
    // Erster Tag und heute eingeschlossen — wie das Tempo der Karte.
    const tempo = buchungen.filter(b => b.tag >= erster).reduce((s, b) => s + b.menge, 0) / (beobachtet + 1);
    if (tempo <= 0) return null;
    const erwartet = z.used + tempo * restTage;
    if (erwartet > z.contracted + 0.01) {
        const bisAufgebraucht = Math.ceil((z.contracted - z.used) / tempo);
        return { text: `aufgebraucht ca. ${datumAnzeige(tagMinus(heute, -bisAufgebraucht)).slice(0, 6)}`, knapp: true };
    }
    return { text: `Prognose Ende ${mengeAnzeige(Math.round(erwartet))} / ${mengeAnzeige(z.contracted)}`, knapp: false };
};

/** YYYY-MM-DD minus n Tage (n negativ = plus), ohne Zeitzone. */
export const tagMinus = (tag: string, n: number): string => {
    const [y, m, d] = tag.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d - n)).toISOString().slice(0, 10);
};

/** Lage eines Tages auf der x-Achse, 0–1. */
export const tagAnteil = (von: string, bis: string, tag: string): number => {
    const gesamt = tageBis(von, bis) ?? 0;
    if (gesamt <= 0) return 0;
    return begrenzt(((tageBis(von, tag) ?? 0) / gesamt) * 100) / 100;
};

/** Farbe des Gesamtbalkens — dieselbe Regel wie in „Leistung & Budget". */
export const gesamtBalkenFarbe = (prozent: number): string =>
    prozent > 100 ? 'bg-red-500' : (prozent > 85 ? 'bg-orange-400' : 'bg-emerald-500');

// ─── Anzeige ────────────────────────────────────────────────────────────────

/** YYYY-MM-DD bzw. DD.MM.YYYY → TT.MM.JJJJ, ohne Zeitzonenumrechnung. */
export const datumAnzeige = (s: string | null | undefined): string => {
    const t = (s || '').trim();
    let m = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
    m = t.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (m) return `${m[1].padStart(2, '0')}.${m[2].padStart(2, '0')}.${m[3]}`;
    return '–';
};

/** „03.03. – 10.04.2026" (gleiches Jahr) bzw. „15.12.2025 – 10.04.2026". */
export const zeitraumAnzeige = (von: string, bis: string): string => {
    const a = datumAnzeige(von);
    const b = datumAnzeige(bis);
    return a.slice(6) === b.slice(6) && a !== '–' ? `${a.slice(0, 6)} – ${b}` : `${a} – ${b}`;
};

/** Menge mit deutschem Komma, hoechstens zwei Nachkommastellen (Stunden). */
export const mengeAnzeige = (n: number): string =>
    new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(n);
